import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { Box, Typography, Grid, Paper, Button, Divider, IconButton, Stack } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";
import { DynamicFieldAdvanced } from "../../../../../../components/common/input/DynamicFieldAdvanced";
import { useDispatch, useSelector } from "react-redux";
import { setBasicDetails, setBasicDetailsComplete } from "../../../../../../store/index";
import { getEntities } from "../../../../../../api/entityApi";
import { getDepartments } from "../../../../../../api/departmentApi";
import { getLocations } from "../../../../../../api/locationApi";
import { getDesignations } from "../../../../../../api/designationApi";
import { useGetRolesQuery } from "../../../../../../services/roleApi";
import { createEmployee, updateEmployee, getEmployees, uploadEmployeeFile } from "../../../../../../api/employeeApi";
import { CircularProgress } from "@mui/material";
import { useNotification } from "../../../../../../hooks/useNotification";

const visuallyHiddenStyles = {
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
};

const FileList = ({ files, onRemove }) => {
  if (!files || files.length === 0) return null;
  return (
    <Stack spacing={1} sx={{ mt: 1 }}>
      {files.map((file, idx) => (
        <Box
          key={idx}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1
          }}
        >
          <Typography variant="body2">{file.name}</Typography>
          <IconButton size="small" onClick={() => onRemove(idx)} sx={{ p: 0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}
    </Stack>
  );
};
const debounce = (func, delay) => {
  let timer;
  return (...args) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      func(...args);
    }, delay);
  };
};

const BasicDetails = ({ onNext, preFilledData, employeeId }) => {
  const dispatch = useDispatch();
  const { showNotification, Notification } = useNotification();
  const formDataRedux = useSelector((state) => state.basicDetails.formData) || {};
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    mobileNumber: "",
    emailAddress: "",
    entity: "",
    jobLocation: "",
    department: "",
    systemRole: "",
    designation: "",
    dateOfJoining: "",
    employmentType: "Full-time",
    employeeId: "",
    managerId: "",
    isFresher: false,
    hasExistingPF: false,
    passportPhotoPath: "",
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const [entities, setEntities] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [locations, setLocations] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isPhotoUploading, setIsPhotoUploading] = useState(false);

  const {
    data: roles = [],
    isLoading: isRolesLoading,
  } = useGetRolesQuery({ status: 'Active' }, {
    refetchOnMountOrArgChange: true
  });

  const { user } = useSelector((state) => state.auth);

  const roleLevelToNumber = (level) => {
    if (!level) return 99;
    const match = String(level).match(/(\d+)/);
    return match ? parseInt(match[0], 10) : 99;
  };

  const activeRoles = useMemo(() => {
    const filtered = roles.filter(role => role.status === 'Active' && role.code !== 'SUPER_ADMIN');
    if (user && user.role && user.role.hierarchyLevel) {
      const creatorLevel = roleLevelToNumber(user.role.hierarchyLevel);
      return filtered.filter(role => roleLevelToNumber(role.hierarchyLevel) >= creatorLevel);
    }
    return filtered;
  }, [roles, user]);

  // Debounced search query functions calling the backend APIs (wrapped in useMemo to persist timers)
  const searchEntities = useMemo(() => debounce(async (search) => {
    try {
      const res = await getEntities({ search, status: 'Active' });
      if (res.success) setEntities(res.data);
    } catch (err) {
      console.error("Error searching entities:", err);
    }
  }, 300), []);

  const searchLocations = useMemo(() => debounce(async (entityId, search) => {
    if (!entityId) return;
    try {
      const res = await getLocations({ entityId, search });
      if (res.success) setLocations(res.data);
    } catch (err) {
      console.error("Error searching locations:", err);
    }
  }, 300), []);

  const searchDepartments = useMemo(() => debounce(async (search) => {
    try {
      const res = await getDepartments({ search, isActive: true });
      if (res.success) setDepartments(res.data);
    } catch (err) {
      console.error("Error searching departments:", err);
    }
  }, 300), []);

  const searchDesignations = useMemo(() => debounce(async (search) => {
    try {
      const res = await getDesignations({ search });
      if (res.success) setDesignations(res.data);
    } catch (err) {
      console.error("Error searching designations:", err);
    }
  }, 300), []);

  const searchManagers = useMemo(() => debounce(async (entityId, deptId, locId, search) => {
    try {
      const res = await getEmployees({
        search,
        entityId,
        departmentId: deptId,
        jobLocation: locId,
        status: 'Active'
      });
      if (res.success) setAllEmployees(res.data);
    } catch (err) {
      console.error("Error searching managers:", err);
    }
  }, 300), []);

  // Trigger server-side filtering of managers when dependencies change
  useEffect(() => {
    const fetchFilteredManagers = async () => {
      try {
        const res = await getEmployees({
          entityId: formData.entity,
          departmentId: formData.department,
          jobLocation: formData.jobLocation,
          status: 'Active'
        });
        if (res.success) {
          setAllEmployees(res.data);
        }
      } catch (error) {
        console.error('Error fetching filtered managers:', error);
      }
    };
    fetchFilteredManagers();
  }, [formData.entity, formData.department, formData.jobLocation]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const [entitiesRes, departmentsRes, designationsRes, employeesRes] = await Promise.all([
          getEntities(),
          getDepartments(),
          getDesignations(),
          getEmployees()
        ]);

        if (entitiesRes.success) setEntities(entitiesRes.data);
        if (departmentsRes.success) setDepartments(departmentsRes.data);
        if (designationsRes.success) setDesignations(designationsRes.data);
        if (employeesRes.success) setAllEmployees(employeesRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Initialize form with pre-filled or loaded employee data
  useEffect(() => {
    // Priority 1: Data from Redux (for edit mode or after save)
    if (formDataRedux && Object.keys(formDataRedux).some(key => formDataRedux[key])) {
      setFormData(prev => ({
        ...prev,
        ...formDataRedux
      }));
    }
    // Priority 2: Pre-filled data from URL/Onboarding
    else if (preFilledData && Object.keys(preFilledData).some(key => preFilledData[key])) {
      setFormData(prev => ({
        ...prev,
        ...Object.entries(preFilledData).reduce((acc, [key, value]) => {
          if (value && value.trim() !== '') {
            acc[key] = value;
          }
          return acc;
        }, {})
      }));
    }
  }, [preFilledData, formDataRedux]);

  useEffect(() => {
    const fetchLocations = async () => {
      if (formData.entity) {
        try {
          const response = await getLocations({ entityId: formData.entity });
          if (response.success) {
            setLocations(response.data);
          } else {
            setLocations([]);
          }
        } catch (error) {
          console.error('Error fetching locations:', error);
          setLocations([]);
        }
      } else {
        setLocations([]);
      }
    };
    fetchLocations();
  }, [formData.entity]);

  const handleFieldChange = useCallback((name, value) => {
    // Clear error for this field when user types
    setErrors((prev) => {
      if (prev[name]) {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      }
      return prev;
    });

    // Update local state instead of Redux
    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'entity' ? { jobLocation: '' } : {})
    }));
  }, []);

  const handlePhotoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!allowedTypes.includes(file.type)) {
      showNotification("Only JPG, JPEG, and PNG images are allowed", "error");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      showNotification("File size must be less than 5MB", "error");
      return;
    }

    const uploadData = new FormData();
    uploadData.append("file", file);

    setIsPhotoUploading(true);
    try {
      const response = await uploadEmployeeFile(uploadData);
      if (response.success && response.data?.url) {
        handleFieldChange("passportPhotoPath", response.data.url);
        showNotification("Passport photo uploaded successfully", "success");
      } else {
        showNotification(response.message || "Failed to upload passport photo", "error");
      }
    } catch (error) {
      console.error("Error uploading passport photo:", error);
      showNotification("Failed to upload passport photo", "error");
    } finally {
      setIsPhotoUploading(false);
    }
  };

  const handleRemovePhoto = () => {
    handleFieldChange("passportPhotoPath", "");
  };

  const validate = () => {
    const newErrors = {};

    // Required fields validation
    if (!formData.firstName?.trim()) {
      newErrors.firstName = "First name is required";
    }

    if (!formData.lastName?.trim()) {
      newErrors.lastName = "Last name is required";
    }

    if (!formData.mobileNumber?.trim()) {
      newErrors.mobileNumber = "Mobile number is required";
    } else if (!/^\d{10}$/.test(formData.mobileNumber.replace(/\D/g, ''))) {
      newErrors.mobileNumber = "Mobile number must be 10 digits";
    }

    if (!formData.emailAddress?.trim()) {
      newErrors.emailAddress = "Email address is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailAddress)) {
      newErrors.emailAddress = "Invalid email format";
    }

    if (!formData.entity) {
      newErrors.entity = "Entity is required";
    }

    if (!formData.jobLocation) {
      newErrors.jobLocation = "Job location is required";
    }

    if (!formData.department) {
      newErrors.department = "Department is required";
    }

    if (!formData.systemRole) {
      newErrors.systemRole = "System role is required";
    }

    if (!formData.designation) {
      newErrors.designation = "Designation is required";
    }

    setErrors(newErrors);
    dispatch(setBasicDetailsComplete(Object.keys(newErrors).length === 0));
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    // Mark all required fields as touched
    const requiredFields = [
      'firstName', 'lastName', 'mobileNumber', 'emailAddress',
      'entity', 'jobLocation', 'department', 'systemRole',
      'designation', 'employeeId'
    ];

    const touchedFields = {};
    requiredFields.forEach(field => {
      touchedFields[field] = true;
    });
    setTouched(touchedFields);

    if (validate()) {
      try {
        // Save form data to Redux before submitting
        dispatch(setBasicDetails(formData));

        // Prepare payload
        const payload = {
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          email: formData.emailAddress.trim(),
          phone: formData.mobileNumber.trim(),
          entityId: formData.entity,
          departmentId: formData.department,
          roleId: formData.systemRole,
          designationId: formData.designation,
          workLocation: formData.jobLocation,
          dateOfJoining: formData.dateOfJoining || null,
          employmentType: formData.employmentType || 'Full-time',
          employeeId: formData.employeeId?.trim(),
          managerId: formData.managerId || null,
          initiateOnBoarding: false,
          status: 'Active',
          collectDocumentsBeforeOffer: false,
          isFresher: formData.isFresher === true || formData.isFresher === 'true' || formData.isFresher === 'yes',
          hasExistingPF: formData.hasExistingPF === true || formData.hasExistingPF === 'true' || formData.hasExistingPF === 'yes',
          passportPhotoPath: formData.passportPhotoPath || null,
        };

        // Submit to backend
        let response;
        if (employeeId) {
          response = await updateEmployee(employeeId, payload);
        } else {
          response = await createEmployee(payload);
        }

        if (response.success) {
          const empId = response.data.id || response.data.employeeId;
          const formalId = response.data.employeeId;

          // Store formal employee ID in Redux for UI consistency
          if (formalId) {
            dispatch(setBasicDetails({ employeeId: formalId }));
          }

          // Mark basic details as complete
          dispatch(setBasicDetailsComplete(true));

          // Clear form states after successful submission
          setFormData({
            firstName: "",
            lastName: "",
            mobileNumber: "",
            emailAddress: "",
            entity: "",
            jobLocation: "",
            department: "",
            systemRole: "",
            designation: "",
            employmentType: "Full-time",
            managerId: "",
            passportPhotoPath: "",
            dateOfJoining: "",
          });
          setErrors({});
          setTouched({});

          if (employeeId) {
            showNotification("Basic details updated successfully", "success");
          }
          if (onNext) {
            onNext(empId || employeeId);
          }
        } else {
          showNotification(response.message || "Failed to update employee", "error");
        }
      } catch (error) {
        console.error('Error submitting basic details:', error);
        showNotification(error.message || "Failed to submit details", "error");
      }
    }
  };

  const handleSkip = () => {
    if (onNext) {
      onNext();
    }
  };

  const NoteBox = () => (
    <Box
      sx={{
        p: 2,
        borderRadius: '10px',
        borderLeft: '4px solid #7522D1',
        bgcolor: '#F9F5FE',
        mb: 3.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#7522D1', fontSize: '0.85rem' }}>
        Core Setup Information
      </Typography>
      <Typography variant="body2" sx={{ color: '#42374F', fontWeight: 500, fontSize: '0.8rem' }}>
        Please enter the basic details of the employee. All fields marked with an asterisk (<span style={{ color: '#EF4444', fontWeight: 700 }}>*</span>) are mandatory to proceed.
      </Typography>
    </Box>
  );

  if (isLoading || isRolesLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#030007', mb: 2.5, display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Box sx={{
          width: 32,
          height: 32,
          borderRadius: '8px',
          bgcolor: '#F5EEFC',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <AssignmentIndOutlinedIcon sx={{ color: '#7522D1', fontSize: 18 }} />
        </Box>
        Core Identity & Assignment
      </Typography>
      <NoteBox />

      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
        <Box sx={{ position: 'relative' }}>
          <Box
            sx={{
              width: 110,
              height: 110,
              borderRadius: '50%',
              border: '2px solid #7522D1',
              boxShadow: '0 4px 14px rgba(117, 34, 209, 0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              bgcolor: '#F5EEFC',
              cursor: 'pointer',
              transition: 'all 0.3s ease-in-out',
              position: 'relative',
              '&:hover .upload-overlay': {
                opacity: 1,
              },
              '&:hover': {
                transform: 'scale(1.02)',
              }
            }}
            component="label"
          >
            {formData.passportPhotoPath ? (
              <img
                src={formData.passportPhotoPath}
                alt="Profile Preview"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', color: '#7522D1' }}>
                {isPhotoUploading ? (
                  <CircularProgress size={28} sx={{ color: '#7522D1' }} />
                ) : (
                  <CloudUploadIcon sx={{ fontSize: 32 }} />
                )}
              </Box>
            )}
            
            {/* Upload Overlay */}
            {!isPhotoUploading && (
              <Box
                className="upload-overlay"
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  bgcolor: 'rgba(117, 34, 209, 0.65)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#FFFFFF',
                  opacity: 0,
                  transition: 'opacity 0.25s ease',
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem', textAlign: 'center', px: 1 }}>
                  {formData.passportPhotoPath ? 'Change Photo' : 'Upload Photo'}
                </Typography>
              </Box>
            )}

            <input
              type="file"
              accept="image/jpeg,image/jpg,image/png"
              onChange={handlePhotoUpload}
              style={visuallyHiddenStyles}
              disabled={isPhotoUploading}
            />
          </Box>

          {/* Floating Delete Button */}
          {formData.passportPhotoPath && !isPhotoUploading && (
            <IconButton
              size="small"
              onClick={handleRemovePhoto}
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                bgcolor: '#EF4444',
                color: '#FFFFFF',
                boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                width: 24,
                height: 24,
                p: 0,
                minWidth: 0,
                '&:hover': {
                  bgcolor: '#DC2626',
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          )}
        </Box>
        <Typography variant="caption" sx={{ mt: 1.5, color: '#6B7280', fontWeight: 500 }}>
          {isPhotoUploading ? 'Uploading picture...' : 'Passport Photo (Max 4MB)'}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="text"
            label="Employee ID"
            name="employeeId"
            value={formData.employeeId || ""}
            onChange={handleFieldChange}
            error={touched.employeeId && !!errors.employeeId}
            errorText={touched.employeeId ? errors.employeeId : ""}
            placeholder="Enter employee ID "
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="text"
            label="First Name"
            name="firstName"
            value={formData.firstName || ""}
            onChange={handleFieldChange}
            error={touched.firstName && !!errors.firstName}
            errorText={touched.firstName ? errors.firstName : ""}
            required
            placeholder="Enter first name"
          />
        </Grid>

        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="text"
            label="Last Name"
            name="lastName"
            value={formData.lastName || ""}
            onChange={handleFieldChange}
            error={touched.lastName && !!errors.lastName}
            errorText={touched.lastName ? errors.lastName : ""}
            required
            placeholder="Enter last name"
          />
        </Grid>

        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="tel"
            label="Mobile Number"
            inputProps={{ maxLength: 10 }}
            name="mobileNumber"
            value={formData.mobileNumber || ""}
            onChange={handleFieldChange}
            error={touched.mobileNumber && !!errors.mobileNumber}
            errorText={touched.mobileNumber ? errors.mobileNumber : ""}
            required
            placeholder="Enter 10-digit mobile number"
          />
        </Grid>

        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="email"
            label="Email Address"
            name="emailAddress"
            value={formData.emailAddress || ""}
            onChange={handleFieldChange}
            error={touched.emailAddress && !!errors.emailAddress}
            errorText={touched.emailAddress ? errors.emailAddress : ""}
            required
            placeholder="Enter email address"
          />
        </Grid>

        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="autocomplete"
            label="Entity"
            name="entity"
            value={formData.entity || ""}
            onChange={handleFieldChange}
            onInputChange={(event, value, reason) => {
              if (reason === 'input') searchEntities(value);
            }}
            error={touched.entity && !!errors.entity}
            errorText={touched.entity ? errors.entity : ""}
            required
            options={entities.map(e => ({ value: e.id || e._id, label: e.name }))}
          />
        </Grid>

        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="autocomplete"
            label="Job Location"
            name="jobLocation"
            value={formData.jobLocation || ""}
            onChange={handleFieldChange}
            onInputChange={(event, value, reason) => {
              if (reason === 'input') searchLocations(formData.entity, value);
            }}
            error={touched.jobLocation && !!errors.jobLocation}
            errorText={touched.jobLocation ? errors.jobLocation : ""}
            required
            disabled={!formData.entity}
            options={locations.map(loc => ({ value: loc.id || loc._id, label: loc.name }))}
            helperText={!formData.entity ? "Select an entity first" : ""}
          />
        </Grid>

        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="autocomplete"
            label="Department"
            name="department"
            value={formData.department || ""}
            onChange={handleFieldChange}
            onInputChange={(event, value, reason) => {
              if (reason === 'input') searchDepartments(value);
            }}
            error={touched.department && !!errors.department}
            errorText={touched.department ? errors.department : ""}
            required
            options={departments.map(d => ({ value: d.id || d._id, label: d.name }))}
          />
        </Grid>

        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="select"
            label="System Role"
            name="systemRole"
            value={formData.systemRole || ""}
            onChange={handleFieldChange}
            error={touched.systemRole && !!errors.systemRole}
            errorText={touched.systemRole ? errors.systemRole : ""}
            required
            options={activeRoles.map(r => ({ value: r.id || r._id, label: r.name }))}
          />
        </Grid>

        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="autocomplete"
            label="Designation"
            name="designation"
            value={formData.designation || ""}
            onChange={handleFieldChange}
            onInputChange={(event, value, reason) => {
              if (reason === 'input') searchDesignations(value);
            }}
            error={touched.designation && !!errors.designation}
            errorText={touched.designation ? errors.designation : ""}
            required
            options={designations.map(d => ({ value: d.id || d._id, label: d.name }))}
          />
        </Grid>

        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="autocomplete"
            label="Reporting Manager"
            name="managerId"
            value={formData.managerId || ""}
            onChange={handleFieldChange}
            onInputChange={(event, value, reason) => {
              if (reason === 'input') searchManagers(formData.entity, formData.department, formData.jobLocation, value);
            }}
            options={allEmployees.map(e => ({ value: e.id || e._id, label: `${e.firstName} ${e.lastName}` }))}
            placeholder="Select a Reporting Manager"
          />
        </Grid>

        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="select"
            label="Employment Type"
            name="employmentType"
            value={formData.employmentType || "Full-time"}
            onChange={handleFieldChange}
            options={[
              { value: "Full-time", label: "Full-time" },
              { value: "Part-time", label: "Part-time" },
              { value: "Contract", label: "Contract" },
              { value: "Intern", label: "Intern" },
            ]}
          />
        </Grid>

        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="date"
            label="Date of Joining"
            name="dateOfJoining"
            value={formData.dateOfJoining || ""}
            onChange={handleFieldChange}
          />
        </Grid>
      </Grid>



      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>

        <Button
          variant="contained"
          onClick={handleSubmit}
          size="large"
        >
          {employeeId ? 'Update' : 'Save & Next'}
        </Button>
      </Box>
      <Notification />
    </Box >
  );
};

export default BasicDetails;